'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import { hydrateCallOwnership } from '@/store/slices/callOwnershipSlice';
import { RootState } from '@/store/store';

/**
 * Call Ownership Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for call ownership state.
 * - Debounced writes (500ms) to prevent excessive DB updates
 * - Syncs to Supabase `calls` table (owner_id, owner_name, claimed_at columns)
 * - Last-write-wins conflict resolution with timestamps
 *
 * Data persisted:
 * - ownerId: UUID of the user who owns/claimed the call
 * - ownerName: Display name of the owner
 * - claimedAt: Timestamp when ownership was claimed
 * - isOwner: Derived boolean (cached for convenience)
 *
 * NOTE: owner_id column is UUID REFERENCES auth.users(id). In staging/demo
 * environments where AUTH_ENABLED=false, the ownerId may be a LiveKit
 * participant identity string (not a UUID). We must validate before writing.
 */

/**
 * Check if a string is a valid UUID v4 format.
 * Used to validate owner_id before writing to the calls table,
 * since the column has a FK constraint to auth.users(id).
 */
function isValidUuid(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * The columns the browser may write to `calls` for an ownership change.
 *
 * HOTFIX (prod e2e 2026-10-22, F1): `calls.owner_id` is the SERVER's record of who leads the call (the atomic scope
 * claim in connection-details writes the lead's user id). The live ownership record usually holds a LiveKit participant
 * identity, not a UUID, so this write used to blank owner_id to NULL right after the lead joined, and the next
 * same-organisation officer then took the call over. The browser never clears or moves owner_id: it is included only
 * when the record carries a real user id (the column is UUID REFERENCES auth.users).
 */
export function buildOwnershipUpdate(data: {
  ownerId: string | null;
  ownerName: string | null;
  claimedAt: number | string | null;
}): Record<string, string | null> {
  return {
    ...(isValidUuid(data.ownerId) ? { owner_id: data.ownerId as string } : {}),
    owner_name: data.ownerName,
    claimed_at: data.claimedAt ? new Date(data.claimedAt).toISOString() : null,
  };
}

const SYNC_DEBOUNCE_MS = 500;

interface UseCallOwnershipSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface CallOwnershipSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

interface OwnershipRecord {
  ownerId: string | null;
  ownerName: string | null;
  isOwner: boolean;
  claimedAt: number | null;
  updatedAt: number;
}

/**
 * Hash the durable ownership fields. Shared by the debounced writer (skip
 * redundant syncs) AND the reconnect dirty-guard (#725) so both hash the exact
 * same shape from a single source.
 */
function hashOwnership(o: {
  ownerId: string | null;
  ownerName: string | null;
  claimedAt: number | null;
}): string {
  return JSON.stringify({ ownerId: o.ownerId, ownerName: o.ownerName, claimedAt: o.claimedAt });
}

/**
 * Hook to sync call ownership to Supabase in real-time
 */
export function useCallOwnershipSync(
  options: UseCallOwnershipSyncOptions,
): CallOwnershipSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const ownership = useSelector((state: RootState) => state.callOwnership);

  // Live mirror of current Redux ownership so the reconnect dirty-guard (#725)
  // can read it without widening loadFromSupabase's dependency array.
  const ownershipRef = useRef(ownership);
  ownershipRef.current = ownership;

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasHydratedRef = useRef(false);
  const hydrationPendingRef = useRef(false);
  const stateRef = useReactiveRef<CallOwnershipSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useCallOwnershipSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync ownership to Supabase
   */
  const syncToSupabase = useCallback(async (data: OwnershipRecord) => {
    if (!enabled || !callId) return;

    // Compute hash to avoid redundant syncs
    const hash = hashOwnership({
      ownerId: data.ownerId,
      ownerName: data.ownerName,
      claimedAt: data.claimedAt,
    });

    if (hash === lastSyncedHashRef.current) {
      logger.debug('useCallOwnershipSync', 'Skipping sync - no changes');
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      // Update the calls table with ownership data
      const { error } = await supabase
        .from('calls')
        .update(buildOwnershipUpdate(data))
        .eq('id', dbCallId);

      if (error) {
        throw error;
      }

      lastSyncedHashRef.current = hash;
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useCallOwnershipSync', 'Synced ownership to Supabase', {
        callId,
        ownerId: data.ownerId,
        ownerName: data.ownerName,
      });
    } catch (err: unknown) {
      logger.error('useCallOwnershipSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase]);

  /**
   * Load ownership from Supabase (on mount)
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId || (!opts?.force && hasHydratedRef.current)) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }
    if (hydrationPendingRef.current) return;

    // #725 reconnect dirty-guard: on a FORCED re-sync, preserve the operator's
    // un-flushed ownership change (e.g. a claim made during the blip) rather
    // than clobbering it with DB. The pending debounced write flushes it (last-
    // write-wins). null baseline → never loaded → not dirty → proceed (Obs1).
    if (
      opts?.force &&
      lastSyncedHashRef.current !== null &&
      hashOwnership({
        ownerId: ownershipRef.current.ownerId,
        ownerName: ownershipRef.current.ownerName,
        claimedAt: ownershipRef.current.claimedAt,
      }) !== lastSyncedHashRef.current
    ) {
      logger.info('useCallOwnershipSync', 'Skipping reconnect re-sync — un-flushed local ownership change pending');
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    // Starting a read does not make the initial empty Redux state durable.
    // Otherwise a read slower than the debounce lets that empty state erase
    // the owner already claimed by /api/connection-details.
    hydrationPendingRef.current = true;

    try {
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select('id, owner_id, owner_name, claimed_at')
        .eq('call_id', callId)
        .maybeSingle();

      if (callError && callError.code !== 'PGRST116') {
        throw callError;
      }

      if (!callData?.id) {
        hasHydratedRef.current = true;
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      if (mountedRef.current) {
        const claimedAtMs = callData.claimed_at
          ? new Date(callData.claimed_at).getTime()
          : null;

        const isOwner = !!callData.owner_id && user?.id === callData.owner_id;

        const hydratedData: OwnershipRecord = {
          ownerId: callData.owner_id ?? null,
          ownerName: callData.owner_name ?? null,
          isOwner,
          claimedAt: claimedAtMs,
          updatedAt: Date.now(),
        };

        hasHydratedRef.current = true;
        lastSyncedHashRef.current = hashOwnership({
          ownerId: hydratedData.ownerId,
          ownerName: hydratedData.ownerName,
          claimedAt: hydratedData.claimedAt,
        });
        dispatch(hydrateCallOwnership({
          ownerId: hydratedData.ownerId,
          ownerName: hydratedData.ownerName,
          isOwner: hydratedData.isOwner,
          claimedAt: hydratedData.claimedAt,
          updatedAt: hydratedData.updatedAt,
        }));

        logger.info('useCallOwnershipSync', 'Loaded from Supabase', {
          ownerId: callData.owner_id,
          ownerName: callData.owner_name,
          isOwner,
        });
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useCallOwnershipSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    } finally {
      hydrationPendingRef.current = false;
    }
  }, [enabled, callId, supabase, dispatch, user?.id]);

  // #725: force a re-sync from Supabase on LiveKit reconnect (dirty-guard above
  // preserves an un-flushed local ownership change). isReloadingRef collapses
  // back-to-back reconnects into a single in-flight fetch.
  const isReloadingRef = useRef(false);
  const reloadFromSupabase = useCallback(async () => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      isReloadingRef.current = false;
    }
  }, [loadFromSupabase]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Debounced sync when ownership changes
  useEffect(() => {
    if (!enabled || !callId) return;

    // Skip if we haven't loaded yet (avoid syncing initial empty state)
    if (!hasHydratedRef.current && !ownership.ownerId) return;

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync
    syncTimeoutRef.current = setTimeout(() => {
      const dataToSync: OwnershipRecord = {
        ownerId: ownership.ownerId,
        ownerName: ownership.ownerName,
        isOwner: ownership.isOwner,
        claimedAt: ownership.claimedAt,
        updatedAt: ownership.updatedAt ?? Date.now(),
      };
      syncToSupabase(dataToSync);
    }, debounceMs);
  }, [enabled, callId, ownership, debounceMs, syncToSupabase]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate call ownership from Supabase (for use outside the main sync hook)
 */
export function useCallOwnershipHydration(callId: string): {
  hydrate: () => Promise<void>;
  isHydrated: boolean;
} {
  const dispatch = useDispatch();
  const supabase = createClient();
  const { user } = useAuth();
  const hydratedRef = useRef(false);

  const hydrate = useCallback(async () => {
    if (hydratedRef.current || !callId) return;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id, owner_id, owner_name, claimed_at')
        .eq('call_id', callId)
        .maybeSingle();

      if (callData && (callData.owner_id || callData.owner_name || callData.claimed_at)) {
        const claimedAtMs = callData.claimed_at
          ? new Date(callData.claimed_at).getTime()
          : null;
        const isOwner = user?.id === callData.owner_id;

        dispatch(hydrateCallOwnership({
          ownerId: callData.owner_id,
          ownerName: callData.owner_name,
          isOwner,
          claimedAt: claimedAtMs,
          updatedAt: Date.now(),
        }));

        logger.info('useCallOwnershipHydration', 'Hydrated from Supabase', {
          ownerId: callData.owner_id,
        });
      }

      hydratedRef.current = true;
    } catch (err: unknown) {
      logger.error('useCallOwnershipHydration', 'Hydration failed', { error: err });
    }
  }, [callId, dispatch, supabase, user?.id]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useCallOwnershipSync;
